import { Button } from '@mantine/core'
import { IconCircleCheck, IconGitMerge } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { formatDate, type ComparisonRecord, type RecordSummary } from './reviewFormat'

/** Which side of the merge a record is on; undefined while the reviewer has not chosen. */
export type RecordRole = 'keep' | 'merge' | undefined

const ACCOUNT_LABELS: Record<RecordSummary['accountStatus'], string> = {
  active: 'Active account',
  invited: 'Invited account',
  inactive: 'Inactive account',
}

const Chip = ({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'warn'
}) => (
  <span
    className={`inline-flex rounded-full px-2 py-[2px] text-[11px] font-medium ${
      tone === 'warn' ? 'bg-[#FFF4E0] text-[#B26A00]' : 'bg-[#F1F2F4] text-[#4A5160]'
    }`}
  >
    {children}
  </span>
)

/** Account status (+ disabled login / merged) as chips; shared with the confirmation modal. */
export function AccountStatus({ summary }: { summary: RecordSummary }) {
  return (
    <div className="flex flex-wrap gap-1">
      <Chip>{ACCOUNT_LABELS[summary.accountStatus]}</Chip>
      {summary.loginDisabled && <Chip tone="warn">Login disabled</Chip>}
      {summary.merged && <Chip tone="warn">Merged</Chip>}
    </div>
  )
}

const SignalRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 border-t border-[#F0F1F3] py-2.5 text-[13px]">
    <dt className="text-[#8A94A6]">{label}</dt>
    <dd className="m-0 text-[#1F2733]">{children}</dd>
  </div>
)

function RoleHeader({ role, onKeep }: { role: RecordRole; onKeep?: () => void }) {
  if (role === 'keep') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1E7B46]">
        <IconCircleCheck size={16} stroke={2} aria-hidden />
        Keep this record
      </span>
    )
  }
  if (role === 'merge') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#B26A00]">
        <IconGitMerge size={16} stroke={2} aria-hidden />
        Merge this record into it
      </span>
    )
  }
  return onKeep ? (
    <Button size="xs" variant="default" onClick={onKeep}>
      Keep this record
    </Button>
  ) : null
}

/**
 * One record of the pair: which side of the merge it is on, and the indicators the keep-record
 * recommendation weighs (account, consent, progress notes, identity).
 */
export function RecordColumn({
  record,
  role,
  onKeep,
}: {
  record: ComparisonRecord
  role: RecordRole
  /** Offered while no record is chosen; omitted on a read-only review. */
  onKeep?: () => void
}) {
  const { summary } = record
  const signed = record.consents.filter((consent) => consent.granted)
  const border =
    role === 'keep'
      ? 'border-[#9BD3B0] shadow-[0_0_0_3px_rgba(30,123,70,.08)]'
      : role === 'merge'
        ? 'border-[#F2D3A1]'
        : 'border-[#E7E9EC]'

  return (
    <section
      aria-label={summary.name}
      className={`flex min-w-0 flex-col rounded-[14px] border bg-white px-5 pt-4 pb-2 ${border}`}
    >
      <div className="min-h-[30px]">
        <RoleHeader role={role} onKeep={onKeep} />
      </div>
      <h2 className="mt-2 truncate text-lg font-bold text-[#1F2733]">{summary.name}</h2>
      <p className="mb-3 truncate text-[13px] text-[#6B7280]">{summary.email ?? 'No email'}</p>

      <dl className="m-0">
        <SignalRow label="Account">
          <AccountStatus summary={summary} />
        </SignalRow>
        <SignalRow label="Consent">
          {signed.length === 0 ? (
            <span className="text-[#6B7280]">Not signed</span>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {signed.map((consent, index) => (
                <li key={index}>
                  <span>
                    {consent.signedAt ? `Signed ${formatDate(consent.signedAt)}` : 'Signed'}
                  </span>
                  {consent.organizationName && (
                    <span className="text-[#8A94A6]"> · {consent.organizationName}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SignalRow>
        <SignalRow label="Progress notes">
          {record.progressNotes.length === 0 ? (
            <span className="text-[#6B7280]">None</span>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {record.progressNotes.map((note, index) => (
                <li key={index} className="flex flex-wrap items-center gap-1">
                  {note.date && (
                    <span className="mr-1 text-[#6B7280]">{formatDate(note.date)}</span>
                  )}
                  <Chip>{note.source === 'behuman' ? 'BeHuman' : 'Imported'}</Chip>
                  <Chip>{note.signed ? 'Signed' : 'Draft'}</Chip>
                  {note.encounterFinished && <Chip>Completed visit</Chip>}
                </li>
              ))}
            </ul>
          )}
        </SignalRow>
        <SignalRow label="Identity">
          {record.identity.verified ? (
            <span className="text-[#1E7B46]">Verified</span>
          ) : (
            <span className="text-[#6B7280]">Not verified</span>
          )}
        </SignalRow>
      </dl>
    </section>
  )
}
